import { NgIfContext } from '@angular/common';
import { ChangeDetectorRef, Directive, EmbeddedViewRef, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { IZippyChangeEvent, IZippyEvent, ZippyAbstract, ZippyEventType } from '@df/legacy/ui/zippy/zippy.abstract';

/**
 * Creates directive that works like ng if but relies on zippy state
 *
 * ```html
 * <div *zippyIf="'someGroup.somName'"></div>
 * <div *zippyIf="['someGroup','somName']" [zippyIfInvert]="true"></div>
 * ```
 *
 */
@Directive({
    selector: '[zippyIf]',
    standalone: false
})
export class ZippyIfDirective extends ZippyAbstract {
    private _context: NgIfContext = new NgIfContext();
    private _viewRef: EmbeddedViewRef<NgIfContext> | null = null;

    constructor(
        private _viewContainer: ViewContainerRef,
        private _templateRef: TemplateRef<NgIfContext>,
        private ref: ChangeDetectorRef
    ) {
        super();
    }

    /**
     * Sets class map on attr changes
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input()
    set zippyIf(name: string | string[]) {
        this.parseName(name);
    }

    @Input()
    zippyIfInvert: boolean = false;

    override ngOnInit(): void {
        super.ngOnInit();

        this.apply(this.isOpen());
    }

    /**
     * Event listener calling ngIf when needed
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {IZippyEvent | IZippyChangeEvent} event
     */
    protected onEvent(event: IZippyEvent | IZippyChangeEvent) {
        if (!event.stopped) {
            if (event.type === ZippyEventType.change) {
                if (this.checkEventMatchExact(event)) {
                    this.apply((<IZippyChangeEvent>event).visible);
                }
            }
        }
    }

    /**
     * Applies classes from classMap depengin on request state
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {boolean} visible [description]
     */
    protected apply(visible: boolean) {
        this._context.$implicit = this._context.ngIf = this.zippyIfInvert ? !visible : visible;

        if (this._context.$implicit) {
            if (!this._viewRef) {
                this._viewContainer.clear();
                if (this._templateRef) {
                    this._viewRef = this._viewContainer.createEmbeddedView(this._templateRef, this._context);
                }
            }
        } else {
            this._viewContainer.clear();
            this._viewRef = null;
        }
        this.ref.detectChanges();
    }
}
